import { useTranslation } from "react-i18next";
import type { LabelBatch } from "./api";

export interface LabelLayout {
  rows: number;
  columns: number;
  margin_pt: number;
  module_pt: number;
}

/** Mirror app.inventory._validate_pdf_layout, in PostScript points. */
export function printableLabelLayout(
  layout: LabelLayout,
  batch?: LabelBatch,
): boolean {
  const { rows, columns, margin_pt: margin, module_pt: module } = layout;
  if (
    !Number.isInteger(rows) ||
    rows < 1 ||
    rows > 10 ||
    !Number.isInteger(columns) ||
    columns < 1 ||
    columns > 3 ||
    !Number.isFinite(margin) ||
    margin < 18 ||
    margin > 72 ||
    !Number.isFinite(module) ||
    module < 0.75 ||
    module > 2
  )
    return false;
  const width = (595 - 2 * margin) / columns - 8;
  const height = (842 - 2 * margin - 50) / rows - 8;
  if (height < 60 + 30 + 22) return false;
  if ((batch?.batch_id.length ?? 18) * 7 > width - 12) return false;
  // Metadata-only pages use the generator's maximum: BX- plus 12 characters.
  // Exact-target reads before download additionally check legacy/longer codes.
  const lengths = batch?.codes?.length
    ? batch.codes.map((code) => code.length)
    : [15];
  return lengths.every((length) => {
    // Set B: start + payload + checksum (11 each), stop (13), TWO quiet zones (10 each).
    const barcodeWidth = (11 * (length + 2) + 13 + 20) * module;
    return barcodeWidth <= width - 12 && length * 9 <= width - 12;
  });
}

const en = {
  pdfError:
    "Could not download the PDF. No stock was changed. Check access and retry.",
  recovery:
    "Issuance is unresolved. Quantity is locked. Review and confirm to recover the same batch before issuing new labels.",
  layoutError:
    "This layout cannot fit the barcode, quiet zones and identifying text. Reduce rows, columns, margin or module size.",
};
type LabelText = { [K in keyof typeof en]: string };
const messages: Record<"en" | "nl" | "fr", LabelText> = {
  en,
  nl: {
    pdfError:
      "De PDF kon niet worden gedownload. Er is geen voorraad gewijzigd. Controleer de toegang en probeer opnieuw.",
    recovery:
      "De uitgifte is nog niet bevestigd. Het aantal is vergrendeld. Controleer en bevestig om dezelfde batch te herstellen voordat u nieuwe etiketten uitgeeft.",
    layoutError:
      "Deze indeling biedt onvoldoende ruimte voor de barcode, stille zones en identificatietekst. Verminder het aantal rijen, kolommen, de marge of de modulegrootte.",
  },
  fr: {
    pdfError:
      "Impossible de télécharger le PDF. Aucun stock n’a été modifié. Vérifiez l’accès et réessayez.",
    recovery:
      "L’émission reste à confirmer. La quantité est verrouillée. Vérifiez et confirmez pour récupérer le même lot avant d’émettre de nouvelles étiquettes.",
    layoutError:
      "Cette disposition ne peut pas contenir le code-barres, les zones de silence et le texte d’identification. Réduisez les lignes, les colonnes, la marge ou la taille du module.",
  },
};
export function useLabelText(): LabelText {
  const { i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  return messages[locale as keyof typeof messages] || en;
}
